import { useCallback, useState } from 'react'
import classnames from 'classnames'

import { Button } from '../Button/Button'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../common/integrations'
import { useAppDispatch } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import styles from './SetDefaultFolderBanner.module.scss'

interface SetDefaultFolderBannerProps {
  serviceId: IntegrationServiceId
  actionText: string
  folderPath: string
  contactFirstName: string
  onSetDefault: () => Promise<boolean>
  className?: string
}

export const SetDefaultFolderBanner = ({ serviceId, actionText, folderPath, contactFirstName, onSetDefault, className }: SetDefaultFolderBannerProps) => {
  const dispatch = useAppDispatch()
  const [isLinking, setIsLinking] = useState<boolean>(false)
  const [isLinked, setIsLinked] = useState<boolean>(false)

  const handleSetDefault = useCallback(async () => {
    setIsLinking(true)
    try {
      const success = await onSetDefault()
      if (success) {
        setIsLinked(true)
        dispatch(
          addToast({
            variant: 'success',
            title: 'Default folder set',
            messages: [`'${folderPath}' was set as ${contactFirstName}'s default folder.`],
          }),
        )
      }
    } finally {
      setIsLinking(false)
    }
  }, [onSetDefault, dispatch, folderPath, contactFirstName])

  if (isLinked) {
    return null
  }

  return (
    <div
      className={classnames(
        'd-flex align-items-center justify-content-between gap-3 px-3 zocks-py-0.75 border zocks-border-color-gray-200 zocks-bg-color-gray-100 zocks-border-0.75',
        className,
      )}
    >
      <div className='d-flex align-items-center gap-3'>
        <i className='bi bi-folder-plus fs-6 zocks-text-gray-900 flex-shrink-0' />
        <div className='paragraph-small zocks-text-gray-900'>
          <div>
            {actionText} {getIntegrationDisplayName(serviceId)}: <span className='bold'>{folderPath}</span>
          </div>
          <div>Set it as {contactFirstName}&rsquo;s default folder?</div>
        </div>
      </div>
      <Button
        className={classnames(
          styles.action,
          'flex-shrink-0 px-2 py-1 paragraph-small bold zocks-text-gray-900 zocks-bg-color-white border zocks-border-color-gray-300 zocks-border-0.5',
        )}
        onClick={handleSetDefault}
        loading={isLinking}
      >
        Set as default folder
      </Button>
    </div>
  )
}
